import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Tooltip,
  CircularProgress,
  Alert,
  Snackbar,
  Grid,
  MenuItem,
  Switch,
  FormControlLabel
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Add, Refresh, Delete, Edit, ToggleOn, ToggleOff, ChevronLeft, ChevronRight } from '@mui/icons-material';
import {
  getServiceTypes,
  createServiceType,
  updateServiceType,
  deleteServiceType,
  toggleServiceType
} from '../../services/fulfillment';

const ServiceTypes = () => {
  const { t } = useTranslation(['fulfillment', 'common', 'sidebar']);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [search, setSearch] = useState('');
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);

  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({
    name: '',
    code: '',
    description: '',
    is_active: true
  });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, item: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const totalPages = Math.ceil(totalItems / rowsPerPage) || 1;

  const loadServiceTypes = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        page_size: rowsPerPage,
        search: search || undefined
      };
      const response = await getServiceTypes(params);
      if (response.results) {
        setServiceTypes(response.results);
        setTotalItems(response.count || 0);
      } else if (Array.isArray(response)) {
        setServiceTypes(response);
        setTotalItems(response.length);
      }
    } catch (error) {
      console.error('Error loading service types:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || t('fulfillment:serviceTypes.messages.failed'),
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, t]);

  useEffect(() => {
    loadServiceTypes();
  }, [loadServiceTypes]);

  const handleOpen = (item = null) => {
    if (item) {
      setEditId(item.sid);
      setForm({
        name: item.name || '',
        code: item.code || '',
        description: item.description || '',
        is_active: item.is_active !== undefined ? item.is_active : true
      });
    } else {
      setEditId(null);
      setForm({
        name: '',
        code: '',
        description: '',
        is_active: true
      });
    }
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setEditId(null);
    setForm({
      name: '',
      code: '',
      description: '',
      is_active: true
    });
  };

  const handleSave = async () => {
    if (!form.name || !form.code) {
      setSnackbar({
        open: true,
        message: t('common:validation.required'),
        severity: 'error'
      });
      return;
    }

    try {
      if (editId) {
        await updateServiceType(editId, form);
        setSnackbar({
          open: true,
          message: t('fulfillment:serviceTypes.messages.updated'),
          severity: 'success'
        });
      } else {
        await createServiceType(form);
        setSnackbar({
          open: true,
          message: t('fulfillment:serviceTypes.messages.created'),
          severity: 'success'
        });
      }
      handleClose();
      loadServiceTypes();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || t('fulfillment:serviceTypes.messages.failed'),
        severity: 'error'
      });
    }
  };

  const handleDeleteClick = (item) => {
    setDeleteDialog({ open: true, item });
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteServiceType(deleteDialog.item.sid);
      setSnackbar({
        open: true,
        message: t('fulfillment:serviceTypes.messages.deleted'),
        severity: 'success'
      });
      setDeleteDialog({ open: false, item: null });
      loadServiceTypes();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || t('fulfillment:serviceTypes.messages.failed'),
        severity: 'error'
      });
    }
  };

  const handleToggle = async (item) => {
    try {
      await toggleServiceType(item.sid);
      setSnackbar({
        open: true,
        message: t('fulfillment:serviceTypes.messages.toggled'),
        severity: 'success'
      });
      loadServiceTypes();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || t('fulfillment:serviceTypes.messages.failed'),
        severity: 'error'
      });
    }
  };

  const handlePrevPage = () => {
    if (page > 0) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    if (page < totalPages - 1) {
      setPage(page + 1);
    }
  };

  const handleRowsPerPageChange = (e) => {
    const value = e.target.value;
    if (value === 'custom') {
      setCustomRowsActive(true);
      setCustomRowsInput(rowsPerPage.toString());
    } else {
      setRowsPerPage(Number(value));
      setPage(0);
      setCustomRowsActive(false);
    }
  };

  const handleCustomRowsInputChange = (e) => {
    setCustomRowsInput(e.target.value.replace(/\D/g, ''));
  };

  const handleCustomRowsInputBlur = () => {
    const val = parseInt(customRowsInput, 10);
    if (val && val > 0) {
      setRowsPerPage(val);
      setPage(0);
    }
    setCustomRowsActive(false);
  };

  const addSerialNumbers = (data, page, rowsPerPage) =>
    data.map((row, idx) => ({
      ...row,
      sn: (page * rowsPerPage) + idx + 1
    }));

  const columns = [
    { field: 'sn', headerName: t('fulfillment:table.sn'), width: 80, sortable: false, filterable: false },
    { field: 'name', headerName: t('fulfillment:serviceTypes.name'), flex: 1, minWidth: 150 },
    { field: 'code', headerName: t('fulfillment:serviceTypes.code'), flex: 1, minWidth: 120 },
    {
      field: 'description',
      headerName: t('fulfillment:serviceTypes.description'),
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {params.value || 'N/A'}
        </Typography>
      )
    },
    {
      field: 'is_active',
      headerName: t('fulfillment:serviceTypes.isActive'),
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value ? t('common:status.active') : t('common:status.inactive')}
          size="small"
          sx={{
            backgroundColor: params.value ? '#4CAF50' : '#F44336',
            color: 'white',
            fontWeight: 'bold'
          }}
        />
      )
    },
    {
      field: 'actions',
      headerName: t('fulfillment:table.actions'),
      width: 180,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title={t('common:edit')}>
            <IconButton color="primary" onClick={() => handleOpen(params.row)}>
              <Edit />
            </IconButton>
          </Tooltip>
          <Tooltip title={params.row.is_active ? t('common:deactivate') : t('common:activate')}>
            <IconButton
              color={params.row.is_active ? 'success' : 'default'}
              onClick={() => handleToggle(params.row)}
            >
              {params.row.is_active ? <ToggleOn /> : <ToggleOff />}
            </IconButton>
          </Tooltip>
          <Tooltip title={t('common:delete')}>
            <IconButton color="error" onClick={() => handleDeleteClick(params.row)}>
              <Delete />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      {/* Header */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 3,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#DC143C' }}>
          {t('fulfillment:serviceTypes.title')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadServiceTypes}
            disabled={loading}
            sx={{ minWidth: 'auto' }}
          >
            {t('common:refresh')}
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' }, minWidth: 'auto' }}
            onClick={() => handleOpen()}
          >
            {t('fulfillment:serviceTypes.add')}
          </Button>
        </Box>
      </Box>

      {/* Search */}
      <Card sx={{ mb: 3, backgroundColor: 'background.paper' }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                label={t('common:search')}
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(0);
                }}
                size="small"
                fullWidth
                placeholder={t('common:searchPlaceholder')}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Table */}
      <Card sx={{ backgroundColor: 'background.paper' }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{
            width: '100%',
            overflowX: 'auto',
            minHeight: 520,
            '&::-webkit-scrollbar': {
              height: 8,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(0,0,0,0.1)',
              borderRadius: 4,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.3)',
              borderRadius: 4,
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: 'rgba(0,0,0,0.5)',
            },
          }}>
            {serviceTypes.length === 0 && !loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', flexDirection: 'column' }}>
                <Typography variant="h6" color="textSecondary" sx={{ mb: 2 }}>
                  {t('fulfillment:table.noData')}
                </Typography>
                <Button variant="outlined" onClick={loadServiceTypes}>
                  {t('common:refresh')}
                </Button>
              </Box>
            ) : (
              <DataGrid
                rows={addSerialNumbers(serviceTypes, page, rowsPerPage)}
                columns={columns}
                getRowId={(row) => row.sid}
                pageSize={rowsPerPage}
                rowsPerPageOptions={[]}
                disableSelectionOnClick
                loading={loading}
                hideFooter
                sx={{
                  border: 'none',
                  minWidth: 800,
                  '& .MuiDataGrid-columnHeader': {
                    backgroundColor: '#DC143C',
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  },
                  '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                    color: 'white'
                  },
                  '& .MuiDataGrid-row:hover': {
                    backgroundColor: '#FFF0F3'
                  },
                  '& .MuiDataGrid-cell': {
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    padding: { xs: '8px 4px', sm: '8px 12px' }
                  }
                }}
              />
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Pagination */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: { xs: 'center', sm: 'flex-end' },
        mt: 3,
        gap: 2,
        flexWrap: 'wrap'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={handlePrevPage} disabled={page === 0}>
            <ChevronLeft />
          </IconButton>
          <Typography variant="body2">
            {t('common:pagination.page')} {totalPages === 0 ? 0 : page + 1} {t('common:pagination.of')} {totalPages}
          </Typography>
          <IconButton onClick={handleNextPage} disabled={page >= totalPages - 1}>
            <ChevronRight />
          </IconButton>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2">
            {t('common:pagination.rowsPerPage')}
          </Typography>
          {!customRowsActive ? (
            <TextField
              select
              size="small"
              value={[10, 20, 50, 100].includes(rowsPerPage) ? rowsPerPage : 'custom'}
              onChange={handleRowsPerPageChange}
              sx={{ width: 100 }}
            >
              {[10, 20, 50, 100].map((option) => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
              <MenuItem value="custom">{t('common:pagination.custom')}</MenuItem>
            </TextField>
          ) : (
            <TextField
              size="small"
              value={customRowsInput}
              onChange={handleCustomRowsInputChange}
              onBlur={handleCustomRowsInputBlur}
              autoFocus
              sx={{ width: 100 }}
              inputProps={{ min: 1, style: { textAlign: 'center' } }}
              placeholder={t('common:pagination.rows')}
            />
          )}
        </Box>
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: '#DC143C', fontWeight: 'bold' }}>
          {editId ? t('fulfillment:serviceTypes.edit') : t('fulfillment:serviceTypes.add')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label={t('fulfillment:serviceTypes.name')}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label={t('fulfillment:serviceTypes.code')}
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
              required
              fullWidth
              disabled={!!editId}
              helperText={t('common:codeHelper')}
            />
            <TextField
              label={t('fulfillment:serviceTypes.description')}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              multiline
              rows={3}
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                />
              }
              label={t('fulfillment:serviceTypes.isActive')}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t('common:cancel')}</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}
          >
            {t('common:save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, item: null })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: '#DC143C', fontWeight: 'bold' }}>
          {t('fulfillment:common.confirmDelete')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {t('fulfillment:common.deleteWarning')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, item: null })}>
            {t('common:cancel')}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}
          >
            {t('common:delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ServiceTypes;

